import React, { useEffect, useState } from 'react';
import { MapPin, Building2, Navigation } from 'lucide-react';
import { getContent } from '@/lib/api';
import { Seo } from '@/components/Seo';
import { PageHero } from '@/components/PageHero';
import { SectionHeading } from '@/components/SectionHeading';
import { TamilNaduMap } from '@/components/TamilNaduMap';
import { Reveal } from '@/components/Reveal';
import { CtaSection } from '@/components/CtaSection';
import { motion, AnimatePresence } from 'framer-motion';

export default function Locations() {
  const [locations, setLocations] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    getContent('locations').then(setLocations).catch(() => {});
  }, []);

  const offices = locations.filter((l) => l.type === 'office');
  const coverage = locations.filter((l) => l.type === 'coverage' && !l.offMap);
  const nearby = locations.filter((l) => l.offMap);

  return (
    <>
      <Seo title='Service Locations' description='Head office in Coimbatore, branches in Karur and Chennai, with service coverage across Tamil Nadu including Tiruppur, Dindigul, Peravurani, Virudhunagar, Musiri, Ulundurpet and Chithode.' path='/locations' image='/media/img/aerial-quarry.jpg' />
      <PageHero eyebrow='Our Service Reach' title='Engineered to operate where the project demands.' subtitle='We clearly distinguish permanent office locations from wider service coverage.' image='/media/img/aerial-quarry.jpg' />

      <section className='relative bg-[#101517] py-20 lg:py-28'>
        <div className='grain absolute inset-0' />
        <div className='relative container-x grid gap-12 lg:grid-cols-12'>
          <div className='lg:col-span-7'>
            <TamilNaduMap locations={locations} activeName={active?.name} onSelect={setActive} />
          </div>
          <div className='lg:col-span-5'>
            <div className='rounded-2xl border border-white/10 bg-[#20272A] p-7 min-h-[220px]'>
              <AnimatePresence mode='wait'>
                {active ? (
                  <motion.div key={active.name} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.35 }} data-testid='service-location-drawer'>
                    <span className={`eyebrow ${active.type === 'office' ? 'text-[#B88A3B]' : 'text-[#7fd4d9]'}`}>{active.label}</span>
                    <h3 className='mt-2 font-head text-3xl font-semibold text-[#F6F5F1]'>{active.name}</h3>
                    {active.address && <p className='mt-4 flex items-start gap-2 text-sm text-[#F6F5F1]/70'><MapPin size={16} className='mt-0.5 shrink-0 text-[#B88A3B]' /> {active.address}</p>}
                    <p className='mt-4 text-sm text-[#F6F5F1]/70'>{active.blurb}</p>
                    <div className='mt-5 flex flex-wrap gap-2'>
                      {(active.services || []).map((s) => <span key={s} className='rounded-full border border-white/12 px-3 py-1 text-xs text-[#F6F5F1]/70'>{s}</span>)}
                    </div>
                  </motion.div>
                ) : (
                  <motion.div key='empty' initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <span className='eyebrow text-[#F6F5F1]/40'>Interactive Map</span>
                    <h3 className='mt-2 font-head text-2xl font-semibold text-[#F6F5F1]'>Select a location</h3>
                    <p className='mt-3 text-sm text-[#F6F5F1]/60'>Gold markers are permanent offices; teal markers indicate service coverage.</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* Offices */}
      <section className='bg-[#F6F5F1] py-20'>
        <div className='container-x'>
          <SectionHeading eyebrow='Office Locations' title='Where we are based.' />
          <div className='mt-12 grid gap-6 md:grid-cols-2'>
            {offices.map((o) => (
              <Reveal key={o.name}>
                <div className='rounded-2xl border border-[#20272A]/10 bg-white p-8'>
                  <div className='flex items-center gap-2 text-[#B88A3B]'><Building2 size={18} /><span className='eyebrow text-[#075B61]'>{o.label}</span></div>
                  <h3 className='mt-3 font-head text-2xl font-semibold text-[#20272A]'>{o.name}</h3>
                  <p className='mt-3 flex items-start gap-2 text-sm text-[#20272A]/70'><MapPin size={16} className='mt-0.5 shrink-0 text-[#075B61]' /> {o.address}</p>
                  <a href={`https://www.google.com/maps/search/${encodeURIComponent(o.address)}`} target='_blank' rel='noreferrer' className='mt-5 inline-flex items-center gap-2 text-sm font-medium text-[#075B61] hover:text-[#B88A3B]'>
                    <Navigation size={15} /> Get Directions
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage */}
      <section className='bg-[#F6F5F1] pb-20'>
        <div className='container-x'>
          <SectionHeading eyebrow='Service Coverage' title='Operating across Tamil Nadu.' intro='Service coverage indicates areas where we execute projects — not necessarily permanent branches.' />
          <div className='mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
            {coverage.map((c) => (
              <div key={c.name} className='rounded-xl border border-[#20272A]/10 bg-white p-5'>
                <div className='flex items-center gap-2'><span className='h-2.5 w-2.5 rounded-full bg-[#0a8a92]' /><h4 className='font-head text-lg font-semibold text-[#20272A]'>{c.name}</h4></div>
                <p className='mt-2 text-xs text-[#20272A]/55'>{(c.services || []).join(', ')}</p>
              </div>
            ))}
          </div>

          {nearby.length > 0 && (
            <div className='mt-10 rounded-2xl border border-[#B88A3B]/25 bg-[#20272A] p-6' data-testid='nearby-state-note'>
              <span className='eyebrow text-[#B88A3B]'>Beyond Tamil Nadu</span>
              <div className='mt-3 flex flex-wrap items-center gap-x-6 gap-y-3'>
                {nearby.map((n) => (
                  <div key={n.name}>
                    <h4 className='font-head text-lg font-semibold text-[#F6F5F1]'>{n.name}</h4>
                    <p className='text-xs text-[#F6F5F1]/55'>{n.region} — {(n.services || []).join(', ')}</p>
                  </div>
                ))}
              </div>
              <p className='mt-3 text-xs text-[#F6F5F1]/45'>Shown separately as it lies outside Tamil Nadu and is not plotted on the state map.</p>
            </div>
          )}
        </div>
      </section>

      <CtaSection />
    </>
  );
}
